import { Component, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';

/**
 * Native HTML5 file drop zone ("arena").
 *
 * Wraps arbitrary projected content (e.g. a table) and turns it into a drop
 * target for files dragged from the operating system. The dashed border and
 * the hint overlay (icon + title + description) are only shown while a file is
 * being dragged over the zone. The overlay text and icon are fully configured
 * from the parent via inputs, so nothing is hardcoded here.
 *
 * This is intentionally NOT built on `@angular/cdk/drag-drop`, which only
 * supports moving elements already rendered inside the application and cannot
 * receive OS file drops.
 */
@Component({
  selector: 'ocp-file-drop-zone',
  imports: [TranslatePipe, ThemedIconComponent],
  templateUrl: './file-drop-zone.component.html',
  styleUrl: './file-drop-zone.component.scss',
  host: {
    class: 'ocp-file-drop-zone',
    '[class.ocp-file-drop-zone--active]': 'isDragOver()',
    '[class.ocp-file-drop-zone--disabled]': 'disabled()',
    '(dragenter)': 'onDragEnter($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)',
  },
})
export class FileDropZoneComponent {
  /** i18n key for the overlay title shown while dragging. */
  readonly overlayTitle = input.required<string>();

  /** i18n key for the overlay description shown while dragging. */
  readonly description = input.required<string>();

  /** Themed icon filename shown in the overlay. Omit to hide the icon. */
  readonly iconName = input<string>();

  /** Disables drop handling and dims the zone. */
  readonly disabled = input<boolean>(false);

  /** Emitted with the dropped files when a valid drop occurs. */
  readonly filesDropped = output<File[]>();

  protected readonly isDragOver = signal(false);

  /**
   * Tracks nested dragenter/dragleave events so the active state does not
   * flicker while the cursor moves over child elements of the zone.
   */
  private dragDepth = 0;

  protected onDragEnter(event: DragEvent): void {
    if (this.disabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.dragDepth++;
    this.isDragOver.set(true);
  }

  protected onDragOver(event: DragEvent): void {
    if (this.disabled()) {
      return;
    }
    // preventDefault is required to allow the element to be a drop target.
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  protected onDragLeave(event: DragEvent): void {
    if (this.disabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.isDragOver.set(false);
    }
  }

  protected onDrop(event: DragEvent): void {
    if (this.disabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.dragDepth = 0;
    this.isDragOver.set(false);
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) {
      this.filesDropped.emit(files);
    }
  }
}
